import { identity, isEqual } from "lodash";
import { createSelector, createSelectorCreator, defaultMemoize } from "reselect";

import type { IDraftSignal, Signal, SignalId } from "app/pages/sales-intelligence/domain/signals/types/signal";
import type { SignalsGroupedById } from "app/pages/sales-intelligence/domain/signals/types/signal-collection";
import isDraftSignal from "app/pages/sales-intelligence/domain/signals/types/type-guards/isDraftSignal";
import { selectSalesIntelligenceState } from "app/pages/sales-intelligence/store/selectors";
import type TrafficSignalFilters from "app/pages/sales-intelligence/sub-modules/signals/models/traffic/TrafficSignalFilters";
import { createStatePropertySelector } from "app/pages/workspace/sales/helpers";
import type { RootState } from "single-spa/store/types";

import signalsService from "../services/signalsService";

const createAppliedSignalsSelector = createSelectorCreator(
    defaultMemoize,
    (prevSignals: readonly (Signal | undefined)[], signals: readonly (Signal | undefined)[]) => {
        function toComparable({ id, filters, signalType, signalSubType }: Signal) {
            return { id, filters, signalType, signalSubType };
        }

        const previousDTOs = prevSignals.filter((s): s is Signal => Boolean(s)).map(toComparable);
        const currentDTOs = signals.filter((s): s is Signal => Boolean(s)).map(toComparable);

        return isEqual(previousDTOs, currentDTOs);
    },
);

const select = createStatePropertySelector(selectSalesIntelligenceState("signals"));

export const selectAppliedSignalsIds = select("appliedSignalsIds");
export const selectShouldAutoApplyCreatedSignal = select("shouldAutoApplyCreatedSignal");
export const selectLastAutoAppliedCreatedSignal = select("lastAutoAppliedCreatedSignal");

export const selectRawSignalsById = select("signalsById");
export const selectSignalsById = createSelector([selectRawSignalsById], signalsService.deduplicateAdminSignals);
export const selectSignalsFetchError = select("signalsFetchError");
export const selectSignalsAreBeingFetched = select("areSignalsBeingFetched");

export const selectSignalsSource = select("signalsSource");

export const selectSignalsDescription = select("signalsDescription");
export const selectSignalsDescriptionIsBeingFetched = select("isSignalsDescriptionBeingFetched");

export const selectSignalsBeingDeleted = select("signalsBeingDeleted");

export const selectSignalUpdateIsInProgress = select("isSignalBeingUpdated");

export const selectIsSignalDropdownOpen = select("isDropdownOpen");
export const selectPreselectedSignalType = select("preselectedSignalType");

export const selectIsSignalsManagementOpen = select("isSignalsManagementOpen");
export const selectPreselectedAccountOverviewSignalType = select("preselectedAccountOverviewSignalType");
export const selectInitialSelectedSignalType = select("initialSelectedSignalType");
export const selectManagementEditSignalId = select("managementEditSignalId");
export const selectManagementDeleteSignalId = select("managementDeleteSignalId");
export const selectSignalIdForHighlight = select("signalIdForHighlight");

export const selectCountPerSignalId = select("countPerSignalId");

export const selectSignalsByIdFromSource = createSelector(
    [selectSignalsById, selectSignalsSource],
    signalsService.filterSignalsBySource,
);

export const selectRawSignalsByIdFromSource = createSelector(
    [selectRawSignalsById, selectSignalsSource],
    signalsService.filterSignalsBySource,
);

export const selectAllSignalsIdsFromSource = createSelector(selectSignalsByIdFromSource, (signalsById) => {
    return Object.keys(signalsById);
});

export const selectRawAllSignalsIdsFromSource = createSelector(selectRawSignalsByIdFromSource, (signalsById) => {
    return Object.keys(signalsById);
});

export const selectAllSignalsFromSource = createSelector(selectSignalsByIdFromSource, (signalsGroupedById) => {
    return Object.keys(signalsGroupedById).map((id) => signalsGroupedById[id]);
});

export const selectNumberOfSignalsFromSource = createSelector(selectAllSignalsIdsFromSource, (allSignalsIds) => {
    return allSignalsIds.length;
});

export const selectDraftSignalsById = createSelector(selectSignalsByIdFromSource, (signalsGroupedById) => {
    return Object.values(signalsGroupedById).reduce<SignalsGroupedById<IDraftSignal>>((acc, signal) => {
        if (isDraftSignal(signal)) {
            acc[signal.id] = signal;
        }

        return acc;
    }, {});
});

export const selectAllDraftSignalsList = createSelector(selectDraftSignalsById, (signalsGroupedById) => {
    return Object.keys(signalsGroupedById).map((id) => signalsGroupedById[id]);
});

export const selectAppliedSignals = createSelector(
    [selectSignalsById, selectAppliedSignalsIds],
    (signalsGroupedById, appliedSignalsIds) => {
        return appliedSignalsIds
            .map((id) => signalsGroupedById[id])
            .filter((signal): signal is Signal => Boolean(signal));
    },
);
export const selectAppliedSignalsMemoized = createAppliedSignalsSelector(selectAppliedSignals, identity) as unknown as (
    s: RootState,
) => Signal[];

export const selectHasAppliedSignals = (state: RootState): boolean => selectAppliedSignalsMemoized(state).length > 0;

export const selectAppliedSignalsGroupedByType = createSelector(
    [selectAppliedSignalsMemoized],
    signalsService.createSignalsDTOsGroupedByType,
);

export const selectAppliedTrafficSignal = (state: RootState): Signal<TrafficSignalFilters> | undefined => {
    const appliedSignalsGroupedByType = selectAppliedSignalsGroupedByType(state);

    return appliedSignalsGroupedByType.Traffic[0];
};

export const selectSignalsGroupedByType = createSelector([selectSignalsById], signalsService.groupSignalsByType);

export const selectSignalIsBeingDeleted = createSelector(
    [selectSignalsBeingDeleted, (_: RootState, signalId: SignalId) => signalId],
    (signalsBeingDeleted, signalId) => signalsBeingDeleted.includes(signalId),
);
